// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { XNGAGE_CUSTOM_WIDGET } from "@conklin/XDL/constants";
import { mergeWithCss } from "@conklin/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";

const enum fields {
    image = "image",
    title = "title",
    description = "description",
    maxNumberOfDescriptionLines = "maxNumberOfDescriptionLines",
    linkLabel = "linkLabel",
    linkUrl = "linkUrl",
    linkSize = "linkSize",
    openLinkInNewTab = "openLinkInNewTab",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.maxNumberOfDescriptionLines]: string;
        [fields.linkLabel]: string;
        [fields.linkUrl]: LinkFieldValue;
        [fields.linkSize]: "small" | "large";
        [fields.openLinkInNewTab]: boolean;
    };
    extendedStyles?: InfoSlideStyles;
    replaceStyles?: boolean;
}

type Props = OwnProps & HasHistory & HasShellContext;

export interface InfoSlideStyles {
    container?: InjectableCss;
    image?: LazyImageProps;
    titleTypography?: TypographyProps;
    descriptionTypography?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    smallLink?: LinkPresentationProps;
    infoWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    imageClickable?: ClickablePresentationProps;
}

export const infoSlideStyles: InfoSlideStyles = {};

const InfoSlideWidget: React.FC<Props> = ({
    fields: { image, title, description, maxNumberOfDescriptionLines, linkLabel, linkUrl, linkSize, openLinkInNewTab },
    extendedStyles,
    replaceStyles = false,
    shellContext,
}) => {
    const [styles] = React.useState(() => mergeWithCss(infoSlideStyles, extendedStyles, replaceStyles));
    const history = useHistory();
    const url = useGetLink(linkUrl).url;

    const goToLink = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (shellContext.isInShell) {
                return;
            }

            openLinkInNewTab ? window.open(url) : (window.location.href = url);
        } else {
            openLinkInNewTab ? window.open(url) : history.push(url);
        }
    };

    return (
        <StyledWrapper {...styles.container}>
            {linkUrl ? (
                <Clickable
                    onClick={goToLink}
                    aria-label={title || "Info Slide Clickable Image"}
                    {...styles.imageClickable}
                >
                    {!!image?.length && (
                        <LazyImage {...styles.image} src={image} altText={title || "Info Slide Image"} />
                    )}
                    {title && <Typography {...styles.titleTypography}>{title}</Typography>}
                </Clickable>
            ) : (
                <StyledWrapper {...styles.imageWrapper}>
                    {!!image?.length && <LazyImage {...styles.image} src={image} altText={title} />}
                    {title && <Typography {...styles.titleTypography}>{title}</Typography>}
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.infoWrapper}>
                {description && (
                    <Typography {...styles.descriptionTypography} {...{ maxNumberOfDescriptionLines }}>
                        {parse(description, parserOptions)}
                    </Typography>
                )}
                {linkLabel && linkUrl && (
                    <Link onClick={goToLink} {...(linkSize === "small" ? styles.smallLink : styles.link)}>
                        {linkLabel}
                    </Link>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(withHistory(InfoSlideWidget)),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.description,
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "Contextual",
            },
            {
                name: fields.maxNumberOfDescriptionLines,
                displayName: "Max Number Of Description Lines",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.linkLabel,
                displayName: "Link Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.linkUrl,
                displayName: "Link URL",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
            },
            {
                name: fields.linkSize,
                displayName: "Link Size",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Small", value: "small" },
                    { displayName: "Large", value: "large" },
                ],
                defaultValue: "small",
                fieldType: "Contextual",
            },
            {
                name: fields.openLinkInNewTab,
                displayName: "Open Link In New Tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
